// Pure helpers for the Venues page. Extracted so the four near-
// identical `filterOptions` callbacks on Autocompletes (and the
// duplicate inline `getOptionLabel` bodies on lines 1482 and 1510
// of VenueCertificates.jsx pre-v10.4.29) become a single tested
// implementation.

// Format a projector catalog entry into a display label. Examples:
//   "Barco SP4K-25 (4K)"
//   "Christie CP4325-RGB"
export function projectorLabel(p) {
  if (!p || typeof p !== 'object') return '';
  const make = p.make || '';
  const model = p.model || '';
  const res = p.native_resolution ? ` (${p.native_resolution})` : '';
  return `${make} ${model}${res}`.trim();
}

// Format a server catalog entry. Same shape, different "max_resolution"
// field name (server-side schema mirrors X-server vs projector-server
// distinction; neither is ours to rename here).
export function serverLabel(s) {
  if (!s || typeof s !== 'object') return '';
  const make = s.make || '';
  const model = s.model || '';
  const res = s.max_resolution ? ` (${s.max_resolution})` : '';
  return `${make} ${model}${res}`.trim();
}

// Autocomplete filterOptions for projector + server pickers. Filters
// the catalog list by `inputValue` substring against (make, model);
// returns the first 50 matches so a long catalog doesn't render
// thousands of DOM nodes inside a popup.
export const EQUIPMENT_FILTER_LIMIT = 50;
export function filterEquipment(opts, inputValue) {
  const list = Array.isArray(opts) ? opts : [];
  const q = (inputValue || '').toLowerCase().trim();
  if (!q) return list.slice(0, EQUIPMENT_FILTER_LIMIT);
  return list
    .filter((o) => o && (
      (o.make || '').toLowerCase().includes(q)
      || (o.model || '').toLowerCase().includes(q)
    ))
    .slice(0, EQUIPMENT_FILTER_LIMIT);
}

// Counter for the Send-Request-Form dialog's "Optional pre-fill"
// accordion header. Returns the number of optional fields the
// operator has populated. Counts a non-empty screen_names entry as
// "1 field" (regardless of how many auditoriums) so the header
// reads "N fields filled" cleanly.
//
// Country defaults to "US" in the initial form state. We exclude
// it from the "fields filled" counter unless the operator actually
// changes it — otherwise the dialog opens reading "1 field filled"
// before the operator has typed anything, which is misleading.
// (Caught by an integration test in v10.4.30.)
export const DEFAULT_COUNTRY = 'US';

// Mirrored exactly by the inline computation in VenueCertificates.jsx
// v10.4.28; this helper makes the math testable and gives the inline
// site one place to consult.
//
// `country` is intentionally NOT in this list — it's auto-seeded to
// "US" so the counter would be wrong from the start. A non-default
// country IS counted via the special case below.
export const PREFILL_KEYS = [
  'chain', 'venue_name', 'server_info', 'projector_info', 'server_serial',
  'street_address', 'city', 'state', 'zip_code', 'tech_contact_phone',
  'timezone', 'version', 'resolution_required',
];

export function countPrefilledRequestFields(form) {
  if (!form || typeof form !== 'object') return 0;
  let count = 0;
  for (const key of PREFILL_KEYS) {
    const value = form[key];
    if (value != null && String(value).trim() !== '') count += 1;
  }
  // Country counts only if changed from the default — otherwise the
  // initial form state would inflate the counter to 1 immediately.
  const country = (form.country || '').trim();
  if (country && country !== DEFAULT_COUNTRY) count += 1;
  // screen_names: count as 1 field if any non-empty row exists.
  const names = Array.isArray(form.screen_names) ? form.screen_names : [];
  if (names.some((s) => (s || '').trim())) count += 1;
  return count;
}
